import React from 'react';
import { ImageRepositoryWidgetConfig } from '../../../../../types';
import { getWidgetIcon } from '../../../registry';

interface ImageRepositoryWidgetBuilderProps {
  config: Partial<ImageRepositoryWidgetConfig>;
  onChange: (config: ImageRepositoryWidgetConfig) => void;
}

const ImageRepositoryWidgetBuilder: React.FC<ImageRepositoryWidgetBuilderProps> = ({
  config,
  onChange,
}) => {
  // Image Repository is a system widget - no configuration needed at template level
  // All settings (images, tags) are defined at dashboard or assignment level

  return (
    <div className="space-y-4">
      <div className="bg-warning-50 border border-warning-200 rounded-lg p-4 text-sm text-warning-800">
        <strong>Image Repository Widget Template</strong>
        <p className="mt-1">
          This creates a reusable image repository widget. No configuration is needed here.
        </p>
      </div>

      {/* Info Box */}
      <div className="border rounded-lg p-4 bg-primary-50 border-primary-200">
        <h4 className="font-medium text-primary-800 mb-2 flex items-center gap-2">
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          How it works
        </h4>
        <ul className="text-sm text-primary-700 space-y-2 ml-7 list-disc">
          <li>Create this template to make image storage available</li>
          <li>When adding to a dashboard, configure the repository:
            <ul className="ml-4 mt-1 list-disc text-primary-600">
              <li><strong>Title</strong> - Name for the image collection</li>
              <li><strong>Data Key</strong> - Unique identifier for storage</li>
            </ul>
          </li>
          <li>Users can upload screenshots and images</li>
          <li>Tag images for easy organization and search</li>
          <li>Browse images with left/right navigation</li>
          <li>Search by title or tags</li>
        </ul>
      </div>

      {/* Preview */}
      <div className="border rounded-lg p-3 bg-surface">
        <label className="block text-sm font-medium text-neutral-700 mb-2">
          Preview
        </label>
        <div className="flex items-center gap-3 p-4 bg-neutral-50 rounded border">
          <span className="text-4xl">{getWidgetIcon('image')}</span>
          <div>
            <p className="font-medium text-neutral-900">Image Repository Widget</p>
            <p className="text-sm text-neutral-500">
              Upload, tag, and browse images within dashboards
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ImageRepositoryWidgetBuilder;
